import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { interestClusterTitle, topClusterScore } from '../../utils/interestClusterDisplay';
import InterestClusterInfoModal from './InterestClusterInfoModal';

/** Softmax scores across 35 clusters always sum to 1 (100%). */
export const INTEREST_CLUSTER_COUNT = 35;
export const INTEREST_CLUSTER_UNIFORM_PCT = (100 / INTEREST_CLUSTER_COUNT).toFixed(1);

/**
 * Horizontal bar chart for ranked interest cluster scores.
 * Labels show true probability (% of 100% split across 35 areas).
 * Bar length is relative to the strongest match in this list for readability.
 * @param {{ clusters: Array<{cluster_id, theme, label, score}>, limit?: number, emptyMessage?: string, showCaption?: boolean, interactive?: boolean }} props
 */
export default function InterestClusterBarChart({
  clusters,
  limit = 8,
  emptyMessage = '',
  showCaption = true,
  interactive = true
}) {
  const { t } = useTranslation();
  const [modalCluster, setModalCluster] = useState(null);
  const [modalRank, setModalRank] = useState(1);
  const top = Array.isArray(clusters) ? clusters.slice(0, limit) : [];

  if (!top.length) {
    return emptyMessage ? (
      <p className="text-sm text-ink-subtle">{emptyMessage}</p>
    ) : null;
  }

  const topScore = topClusterScore(clusters);
  const barTopScore = Math.max(...top.map((c) => c.score || 0), 0.0001);

  return (
    <div>
      {interactive ? (
        <InterestClusterInfoModal
          cluster={modalCluster}
          rank={modalRank}
          topScore={topScore}
          isOpen={Boolean(modalCluster)}
          onClose={() => setModalCluster(null)}
        />
      ) : null}
      {showCaption ? (
        <p className="text-xs text-ink-subtle mb-3">{t('interestCluster.barCaption')}</p>
      ) : null}
      <ul className="space-y-2">
        {top.map((c, index) => {
          const score = c.score || 0;
          const pct = (score * 100).toFixed(1);
          const widthPct = score > 0 ? Math.max(8, (score / barTopScore) * 100) : 0;
          const rank = index + 1;
          const label = interestClusterTitle(c, t);

          if (!interactive) {
            return (
              <li key={`${c.cluster_id}-${c.theme}`}>
                <div className="flex justify-between text-xs text-ink-muted mb-1 gap-2">
                  <span className="font-medium text-ink truncate">{label}</span>
                  <span className="shrink-0 tabular-nums">{pct}%</span>
                </div>
                <div className="h-2.5 bg-edge rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </li>
            );
          }

          return (
            <li key={`${c.cluster_id}-${c.theme}`}>
              <button
                type="button"
                onClick={() => {
                  setModalCluster(c);
                  setModalRank(rank);
                }}
                className="w-full text-left rounded-lg px-1 py-1 -mx-1 hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('interestCluster.popup.openTile', { title: label })}
              >
                <div className="flex justify-between text-xs text-ink-muted mb-1 gap-2">
                  <span className="font-medium text-ink truncate">{label}</span>
                  <span className="shrink-0 tabular-nums">{pct}%</span>
                </div>
                <div className="h-2.5 bg-edge rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
